/**
 * ─────────────────────────────────────────────────────────────
 * SITE CONFIG — static, developer-owned settings.
 *
 * This is the compile-time layer: brand, contact, currency, and
 * *default* opening hours / services. Things the business owner needs
 * to change without a redeploy live in the editable content layer
 * instead (see config/content.registry.ts + lib/content.ts).
 *
 * Rule of thumb:
 *   - changes rarely, set by the developer  → here
 *   - changes often, edited by the owner     → content collection
 * ─────────────────────────────────────────────────────────────
 */
import type { OpeningHour } from '@/lib/types';

export interface SiteConfig {
  name: string;
  legalName: string;
  tagline: string;
  description: string;
  /** Absolute URL of the deployed site (overridden by NEXT_PUBLIC_SITE_URL). */
  url: string;
  locale: string;

  contact: {
    email: string;
    phone: string;
    address: string;
    city: string;
    postalCode: string;
    country: string;
    geo?: { lat: number; lng: number };
  };

  social: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
  };

  /** Brand palette — fed straight into the MUI theme. */
  brand: {
    primary: string;
    secondary: string;
    mode: 'light' | 'dark';
  };

  /** Default opening hours. The owner can override these live via the
   *  `hours` content slot; use resolveHours() to read the effective value. */
  openingHours: OpeningHour[];

  /** Appointment booking defaults. */
  booking: {
    slotMinutes: number;
    horizonDays: number;
    services: string[];
  };

  /** Currency for product prices (ISO 4217). */
  currency: string;
}

export const siteConfig: SiteConfig = {
  name: 'Valérie Nounou',
  legalName: 'Valérie Nounou',
  tagline: 'Assistante maternelle agréée à Vizille, depuis 2001',
  description:
    'Valérie, assistante maternelle agréée depuis 2001 à Vizille, accueille jusqu’à 4 enfants dans sa maison avec jardin, à deux pas de l’école Joliot-Curie. Sorties quotidiennes, activités manuelles, lecture et sieste en chambre individuelle.',
  url: process.env.NEXT_PUBLIC_SITE_URL || 'https://www.valerie-nounou.fr',
  locale: 'fr_FR',

  contact: {
    email: '',
    phone: '06 10 20 20 24',
    address: 'Avenue de Venaria',
    city: 'Vizille',
    postalCode: '38220',
    country: 'FR',
    // Pas de coordonnées précises : numéro de rue inconnu (question ouverte).
  },

  social: {},

  brand: {
    primary: '#5E7D5A',
    secondary: '#E08A4C',
    mode: 'light',
  },

  // Horaires non confirmés — le slot `hours` reste vide tant que Valérie ne les
  // a pas renseignés (question ouverte ; la section contact n'affiche alors que
  // l'adresse et le téléphone).
  openingHours: [],

  booking: {
    slotMinutes: 30,
    horizonDays: 30,
    services: [],
  },

  currency: 'EUR',
};

export default siteConfig;
